<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const props = defineProps({
  agent: { type: Object, default: null },
});

const emit = defineEmits(['save', 'close']);

const TEMPLATES = [
  {
    key: 'sentiment',
    name: () => t('AI_INSIGHTS.AGENTS.FORM.TEMPLATES.SENTIMENT'),
    description: 'Analisa o sentimento geral da conversa',
    system_prompt: `Você é um analista de experiência do cliente. Analise o sentimento geral desta conversa e escreva um parágrafo conciso com:
- O sentimento predominante do cliente (positivo, neutro ou negativo)
- Os principais motivos que indicam esse sentimento
- Uma avaliação do desfecho da conversa

Responda em português, de forma direta e objetiva.`,
  },
  {
    key: 'summary',
    name: () => t('AI_INSIGHTS.AGENTS.FORM.TEMPLATES.SUMMARY'),
    description: 'Resume os pontos principais da conversa',
    system_prompt: `Você é um assistente especializado em resumir atendimentos ao cliente. Leia esta conversa e escreva um resumo claro e objetivo com:
- O motivo do contato
- Os principais pontos discutidos
- O resultado ou status atual

Seja conciso (máximo 3 parágrafos) e escreva em português.`,
  },
  {
    key: 'next_steps',
    name: () => t('AI_INSIGHTS.AGENTS.FORM.TEMPLATES.NEXT_STEPS'),
    description: 'Identifica ações e próximos passos da conversa',
    system_prompt: `Você é um assistente de produtividade. Analise esta conversa e liste os próximos passos identificados, indicando:
- Quem é o responsável por cada ação (atendente ou cliente)
- O que precisa ser feito
- Se há algum prazo ou urgência mencionada

Formate como uma lista numerada, em português.`,
  },
  {
    key: 'objections',
    name: () => t('AI_INSIGHTS.AGENTS.FORM.TEMPLATES.OBJECTIONS'),
    description: 'Mapeia objeções do cliente e como foram tratadas',
    system_prompt: `Você é um analista de vendas. Analise esta conversa e identifique as objeções levantadas pelo cliente. Para cada objeção, descreva:
- Qual foi a objeção
- Como o atendente respondeu (ou se ficou sem resposta)
- Se a objeção foi superada ou não

Ao final, faça uma avaliação geral da receptividade do cliente. Escreva em português.`,
  },
];

const POST_ACTIONS = [
  { value: 'no_action',    labelKey: 'AI_INSIGHTS.AGENTS.FORM.POST_ACTION.NONE' },
  { value: 'add_label',    labelKey: 'AI_INSIGHTS.AGENTS.FORM.POST_ACTION.ADD_LABEL' },
  { value: 'add_note',     labelKey: 'AI_INSIGHTS.AGENTS.FORM.POST_ACTION.ADD_NOTE' },
  { value: 'assign_agent', labelKey: 'AI_INSIGHTS.AGENTS.FORM.POST_ACTION.ASSIGN_AGENT' },
];

const form = ref({
  name: '',
  description: '',
  system_prompt: '',
  model_override: '',
  active: true,
  post_analysis_action: 'no_action',
  action_value: '',
});

watch(
  () => props.agent,
  agent => {
    form.value = agent
      ? {
          name: agent.name || '',
          description: agent.description || '',
          system_prompt: agent.system_prompt || '',
          model_override: agent.model_override || '',
          active: agent.active ?? true,
          post_analysis_action: agent.post_analysis_action || 'no_action',
          action_value: agent.action_value || '',
        }
      : { name: '', description: '', system_prompt: '', model_override: '', active: true, post_analysis_action: 'no_action', action_value: '' };
  },
  { immediate: true }
);

function applyTemplate(templateKey) {
  if (!templateKey) return;
  const tpl = TEMPLATES.find(t => t.key === templateKey);
  if (!tpl) return;
  form.value.name = tpl.name();
  form.value.description = tpl.description;
  form.value.system_prompt = tpl.system_prompt;
}

function submit() {
  if (!form.value.name || !form.value.system_prompt) return;
  emit('save', { ...form.value });
}
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="agent
        ? $t('AI_INSIGHTS.AGENTS.FORM.EDIT_TITLE')
        : $t('AI_INSIGHTS.AGENTS.FORM.CREATE_TITLE')"
      :header-content="agent
        ? $t('AI_INSIGHTS.AGENTS.FORM.EDIT_DESC')
        : $t('AI_INSIGHTS.AGENTS.FORM.CREATE_DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="submit">
      <!-- Template selector (only for new agents) -->
      <div v-if="!agent" class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ $t('AI_INSIGHTS.AGENTS.FORM.TEMPLATES.LABEL') }}
        </label>
        <select
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
          @change="e => applyTemplate(e.target.value)"
        >
          <option value="">{{ $t('AI_INSIGHTS.AGENTS.FORM.TEMPLATES.PLACEHOLDER') }}</option>
          <option v-for="tpl in TEMPLATES" :key="tpl.key" :value="tpl.key">
            {{ tpl.name() }}
          </option>
        </select>
      </div>

      <woot-input
        v-model="form.name"
        class="w-full"
        :label="$t('AI_INSIGHTS.AGENTS.FORM.NAME.LABEL')"
        :placeholder="$t('AI_INSIGHTS.AGENTS.FORM.NAME.PLACEHOLDER')"
        :required="true"
      />
      <woot-input
        v-model="form.description"
        class="w-full"
        :label="$t('AI_INSIGHTS.AGENTS.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('AI_INSIGHTS.AGENTS.FORM.DESCRIPTION.PLACEHOLDER')"
      />
      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ $t('AI_INSIGHTS.AGENTS.FORM.SYSTEM_PROMPT.LABEL') }}
          <span class="text-n-ruby-11">*</span>
        </label>
        <textarea
          v-model="form.system_prompt"
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 h-40 resize-none"
          :placeholder="$t('AI_INSIGHTS.AGENTS.FORM.SYSTEM_PROMPT.PLACEHOLDER')"
          required
        />
      </div>
      <woot-input
        v-model="form.model_override"
        class="w-full"
        :label="$t('AI_INSIGHTS.AGENTS.FORM.MODEL_OVERRIDE.LABEL')"
        :placeholder="$t('AI_INSIGHTS.AGENTS.FORM.MODEL_OVERRIDE.PLACEHOLDER')"
      />
      <!-- Post-analysis action -->
      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ $t('AI_INSIGHTS.AGENTS.FORM.POST_ACTION.LABEL') }}
        </label>
        <select
          v-model="form.post_analysis_action"
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option v-for="action in POST_ACTIONS" :key="action.value" :value="action.value">
            {{ $t(action.labelKey) }}
          </option>
        </select>
      </div>
      <woot-input
        v-if="form.post_analysis_action === 'add_label'"
        v-model="form.action_value"
        class="w-full"
        :label="$t('AI_INSIGHTS.AGENTS.FORM.POST_ACTION.VALUE_LABEL_LABEL')"
        :placeholder="$t('AI_INSIGHTS.AGENTS.FORM.POST_ACTION.VALUE_LABEL_PLACEHOLDER')"
      />
      <woot-input
        v-if="form.post_analysis_action === 'assign_agent'"
        v-model="form.action_value"
        class="w-full"
        :label="$t('AI_INSIGHTS.AGENTS.FORM.POST_ACTION.VALUE_AGENT_LABEL')"
        :placeholder="$t('AI_INSIGHTS.AGENTS.FORM.POST_ACTION.VALUE_AGENT_PLACEHOLDER')"
      />

      <div class="flex items-center w-full gap-2 mb-4">
        <input id="agent-active" v-model="form.active" type="checkbox" />
        <label for="agent-active" class="text-body-main text-n-slate-11">
          {{ $t('AI_INSIGHTS.AGENTS.FORM.ACTIVE') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AI_INSIGHTS.AGENTS.FORM.CANCEL')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AI_INSIGHTS.AGENTS.FORM.SAVE')"
          :disabled="!form.name || !form.system_prompt"
        />
      </div>
    </form>
  </div>
</template>
